import React from "react"
import { cn } from "@/lib/utils"

interface OptimGovLoaderProps {
	size?: "sm" | "md" | "lg"
	className?: string
	fullScreen?: boolean
}

// The 11 ridges of the OptimGov fingerprint, traced from /OptimLogo.png, outermost first.
const RIDGES = [
	"M44.7 42.7A148.6 148.6 0 0 1 167.1 45.7",
	"M13.3 71.9A149.6 149.6 0 0 1 183.3 66.5",
	"M18.9 81.5A139.4 139.4 0 0 1 192.1 86.5",
	"M12.2 101.8A129.5 129.5 0 0 1 195.1 104.5",
	"M19.8 110.1A118.3 118.3 0 0 1 189.6 114.9",
	"M22.6 123.2A107.1 107.1 0 0 1 181.6 122.8",
	"M42.1 120.6A95.2 95.2 0 0 1 173.6 130.9",
	"M47.3 130.8A84.7 84.7 0 0 1 176.8 154.3",
	"M71.2 129.0A72.6 72.6 0 0 1 172.4 172.5",
	"M78.3 138.1A61.6 61.6 0 0 1 162.9 179.7",
	"M104.5 145.0A47.8 47.8 0 0 1 152.8 187.6",
]

const SIZES = { sm: "w-8", md: "w-12", lg: "w-[72px]" }

/**
 * Animated OptimGov fingerprint: the ridges draw in one after another like a
 * scan, hold, then wipe out. Use it for page, panel and dialog loading states;
 * buttons and inline text keep the small Loader2 spinner.
 */
export const OptimGovLoader: React.FC<OptimGovLoaderProps> = ({ size = "md", className, fullScreen = false }) => {
	const loader = (
		<svg
			viewBox='4 34 196 160'
			fill='none'
			stroke='currentColor'
			strokeWidth={7}
			strokeLinecap='round'
			aria-hidden='true'
			className={cn("optimgov-loader h-auto shrink-0 text-primary", SIZES[size], className)}
		>
			<g opacity={0.12}>
				{RIDGES.map((d) => (
					<path key={d} d={d} />
				))}
			</g>
			{RIDGES.map((d, i) => (
				<path key={d} d={d} pathLength={1} style={{ "--i": i } as React.CSSProperties} />
			))}
		</svg>
	)

	if (fullScreen) {
		return (
			<div role='status' aria-label='Loading' className='fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm'>
				{loader}
			</div>
		)
	}

	return loader
}

/**
 * Full-page loader with OptimGov branding, used for route-level loading states.
 */
export const OptimGovPageLoader: React.FC = () => {
	return (
		<div role='status' aria-label='Loading' className='flex min-h-dvh items-center justify-center bg-background'>
			<OptimGovLoader size='lg' className='optimgov-loader-delayed' />
		</div>
	)
}
